import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  Icon,
  Input,
  Table,
  type Column,
} from '@/components/primitives';
import { PageHeader } from '@/components/layout/PageHeader';
import { ConfigCategoryCard } from '@/components/config/ConfigCategoryCard';
import { useViewport } from '@/lib/viewport';
import { useCurrentConfig } from '@/hooks/useConfig';
import { CATEGORY_HELP } from '@/domain/config-help';
import type { ConfigKind, ConfigValue } from '@/domain';

const KIND_LABEL: Record<ConfigKind, string> = {
  MONTHLY_MULTIPLIER: 'Hệ số tháng',
  EVENT_MULTIPLIER: 'Hệ số sự kiện',
  UPH_NORM: 'Định mức UPH',
  CUTOFF_DAYS: 'Cut-off cấp',
};

const KIND_LINK: Record<ConfigKind, string> = {
  MONTHLY_MULTIPLIER: '/admin/config/thamso',
  EVENT_MULTIPLIER: '/admin/config/thamso',
  UPH_NORM: '/admin/config/dinhmuc',
  CUTOFF_DAYS: '/admin/config/thamso',
};

const MONTH_LABELS = ['T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9', 'T10', 'T11', 'T12'];

export function ConfigPage() {
  const { isMobile, vw } = useViewport();
  const navigate = useNavigate();
  const all = useCurrentConfig();
  const [showTable, setShowTable] = useState(false);
  const [q, setQ] = useState('');
  const [kindFilter, setKindFilter] = useState<'all' | ConfigKind>('all');

  const grouped = useMemo(() => {
    const groups: Record<ConfigKind, ConfigValue[]> = {
      MONTHLY_MULTIPLIER: [],
      EVENT_MULTIPLIER: [],
      UPH_NORM: [],
      CUTOFF_DAYS: [],
    };
    for (const v of all) groups[v.kind]?.push(v);
    return groups;
  }, [all]);

  const monthValues = useMemo(() => {
    const m = new Map<number, number>();
    for (const v of grouped.MONTHLY_MULTIPLIER) {
      const month = Number(v.key.split(':')[1]);
      if (!Number.isNaN(month)) m.set(month, v.value);
    }
    return Array.from({ length: 12 }, (_, i) => m.get(i + 1) ?? 1);
  }, [grouped.MONTHLY_MULTIPLIER]);

  const eventList = useMemo(() => {
    return grouped.EVENT_MULTIPLIER.slice()
      .sort((a, b) => b.value - a.value)
      .map((v) => ({ name: v.key.split(':')[1] ?? '', value: v.value }));
  }, [grouped.EVENT_MULTIPLIER]);

  const uphWarehouses = useMemo(() => {
    const m = new Map<string, number>();
    for (const v of grouped.UPH_NORM) {
      const parts = v.key.split(':');
      const wh = parts[1];
      if (wh) m.set(wh, (m.get(wh) ?? 0) + 1);
    }
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
  }, [grouped.UPH_NORM]);

  const rows = useMemo(() => {
    let r = all;
    if (kindFilter !== 'all') r = r.filter((v) => v.kind === kindFilter);
    if (q) {
      const needle = q.toLowerCase();
      r = r.filter((v) => v.key.toLowerCase().includes(needle));
    }
    return r.slice().sort((a, b) => a.key.localeCompare(b.key));
  }, [all, kindFilter, q]);

  const cols: Column<ConfigValue>[] = [
    {
      header: 'Key',
      render: (r) => (
        <span style={{ fontFamily: 'var(--bx-font-mono)', fontSize: 12 }}>{r.key}</span>
      ),
    },
    {
      header: 'Loại',
      priority: 2,
      render: (r) => <Badge kind="INFO">{KIND_LABEL[r.kind]}</Badge>,
    },
    {
      header: 'Giá trị',
      align: 'right',
      mono: true,
      width: 120,
      render: (r) =>
        r.kind === 'UPH_NORM' || r.kind === 'CUTOFF_DAYS' ? r.value : '×' + r.value.toFixed(2),
    },
    {
      header: 'v',
      align: 'right',
      mono: true,
      width: 60,
      priority: 2,
      render: (r) => 'v' + r.version,
    },
    {
      header: 'Hiệu lực từ',
      priority: 3,
      render: (r) => (
        <span style={{ fontFamily: 'var(--bx-font-mono)', fontSize: 12 }}>{r.effective_from}</span>
      ),
    },
    {
      header: 'Bởi',
      priority: 3,
      render: (r) => (
        <span style={{ fontSize: 11.5, color: 'var(--bx-text-muted)' }}>{r.created_by}</span>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <PageHeader
        breadcrumb={['Cấu hình', 'Tham số vận hành']}
        title="Tham số vận hành"
        subtitle="Trung tâm điều khiển hệ số mùa vụ, sự kiện và năng suất. Thay đổi áp dụng cho lần chạy engine kế tiếp."
      />
      <div style={{ flex: 1, padding: isMobile ? 14 : 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Hero strip */}
        <Card style={{ background: 'linear-gradient(135deg, #eef2ff 0%, #f5f3ff 100%)', border: '1px solid #c7d2fe' }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: '#fff',
                color: 'var(--bx-primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Icon name="sliders" size={18} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--bx-text-heading)', marginBottom: 4 }}>
                Cần làm gì ở đây?
              </div>
              <div style={{ fontSize: 13, color: 'var(--bx-text-body)', lineHeight: 1.55 }}>
                Chọn 1 trong 3 nhóm dưới để chỉnh — engine forecast sẽ tự lấy giá trị mới nhất cho lần chạy kế tiếp.
                Mọi thay đổi đều tạo phiên bản mới (không ghi đè), có audit log đầy đủ.
              </div>
            </div>
          </div>
        </Card>

        {/* 3 category cards */}
        <div
          style={{
            display: 'grid',
            gap: 14,
            gridTemplateColumns: vw < 900 ? '1fr' : 'repeat(3, 1fr)',
          }}
        >
          <ConfigCategoryCard
            icon="calendar"
            title={CATEGORY_HELP.MONTHLY_MULTIPLIER.label}
            count={grouped.MONTHLY_MULTIPLIER.length}
            countLabel="/ 12 tháng"
            why={CATEGORY_HELP.MONTHLY_MULTIPLIER.why}
            example={CATEGORY_HELP.MONTHLY_MULTIPLIER.example}
            cta={CATEGORY_HELP.MONTHLY_MULTIPLIER.cta}
            onClick={() => navigate('/admin/config/thamso')}
            tone="primary"
          >
            <MonthlySparkline values={monthValues} />
          </ConfigCategoryCard>

          <ConfigCategoryCard
            icon="spark"
            title={CATEGORY_HELP.EVENT_MULTIPLIER.label}
            count={grouped.EVENT_MULTIPLIER.length}
            countLabel="loại"
            why={CATEGORY_HELP.EVENT_MULTIPLIER.why}
            example={CATEGORY_HELP.EVENT_MULTIPLIER.example}
            cta={CATEGORY_HELP.EVENT_MULTIPLIER.cta}
            onClick={() => navigate('/admin/config/thamso')}
            tone="warning"
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {eventList.slice(0, 4).map((e) => (
                <span
                  key={e.name}
                  style={{
                    fontSize: 11.5,
                    fontFamily: 'var(--bx-font-mono)',
                    padding: '2px 8px',
                    background: '#fff',
                    border: '1px solid var(--bx-border-muted)',
                    borderRadius: 999,
                    color: 'var(--bx-text-body)',
                  }}
                >
                  {e.name} <strong style={{ color: '#c2410c' }}>×{e.value.toFixed(2)}</strong>
                </span>
              ))}
              {eventList.length > 4 && (
                <span style={{ fontSize: 11.5, color: 'var(--bx-text-muted)', alignSelf: 'center' }}>
                  +{eventList.length - 4} loại khác
                </span>
              )}
            </div>
          </ConfigCategoryCard>

          <ConfigCategoryCard
            icon="layers"
            title={CATEGORY_HELP.UPH_NORM.label}
            count={grouped.UPH_NORM.length}
            countLabel="norms"
            why={CATEGORY_HELP.UPH_NORM.why}
            example={CATEGORY_HELP.UPH_NORM.example}
            cta={CATEGORY_HELP.UPH_NORM.cta}
            onClick={() => navigate('/admin/config/dinhmuc')}
            tone="success"
          >
            <div style={{ fontSize: 11.5, color: 'var(--bx-text-muted)', lineHeight: 1.6 }}>
              <div style={{ marginBottom: 4 }}>Phủ {uphWarehouses.length} kho · top:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {uphWarehouses.slice(0, 3).map(([wh, n]) => (
                  <span
                    key={wh}
                    style={{
                      fontSize: 11,
                      fontFamily: 'var(--bx-font-mono)',
                      padding: '2px 8px',
                      background: '#fff',
                      border: '1px solid var(--bx-border-muted)',
                      borderRadius: 999,
                      color: 'var(--bx-text-body)',
                    }}
                  >
                    {wh} <strong style={{ color: '#047857' }}>{n}</strong>
                  </span>
                ))}
              </div>
            </div>
          </ConfigCategoryCard>
        </div>

        {/* Conventions */}
        <Card title="Cần biết trước khi sửa" subtitle="Đọc kỹ — thay đổi config ảnh hưởng toàn bộ forecast">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: 16,
            }}
          >
            <Hint
              icon="layers"
              title="Immutable & versioned"
              body="Mọi thay đổi tạo dòng mới (version + 1). Bản cũ giữ lại để rollback và audit."
            />
            <Hint
              icon="shield"
              title="Quyền sửa"
              body="Chỉ ADMIN có quyền sửa config. Mọi thao tác đều ghi audit_log với actor."
            />
            <Hint
              icon="rocket"
              title="Hiệu lực ngay"
              body="Engine forecast tiếp theo (cron nightly hoặc manual run) sẽ dùng version mới."
            />
          </div>
        </Card>

        {/* Power-user toggle */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="ghost"
            size="sm"
            icon={showTable ? 'chevron-down' : 'chevron-right'}
            onClick={() => setShowTable((v) => !v)}
          >
            {showTable ? 'Ẩn bảng tham số kỹ thuật' : `Xem tất cả ${all.length} tham số (table view)`}
          </Button>
        </div>

        {showTable && (
          <Card padding={0}>
            <div
              style={{
                padding: '12px 14px',
                borderBottom: '1px solid var(--bx-border-muted)',
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ width: 280, minWidth: 200 }}>
                <Input
                  icon="search"
                  placeholder="Tìm theo key…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  size="sm"
                />
              </div>
              <div
                role="tablist"
                style={{
                  display: 'inline-flex',
                  gap: 4,
                  padding: 4,
                  background: 'var(--bx-surface-2)',
                  borderRadius: 10,
                }}
              >
                {(
                  [
                    { id: 'all', label: 'Tất cả', count: all.length },
                    {
                      id: 'MONTHLY_MULTIPLIER' as ConfigKind,
                      label: 'Tháng',
                      count: grouped.MONTHLY_MULTIPLIER.length,
                    },
                    {
                      id: 'EVENT_MULTIPLIER' as ConfigKind,
                      label: 'Sự kiện',
                      count: grouped.EVENT_MULTIPLIER.length,
                    },
                    {
                      id: 'UPH_NORM' as ConfigKind,
                      label: 'UPH',
                      count: grouped.UPH_NORM.length,
                    },
                  ] as { id: 'all' | ConfigKind; label: string; count: number }[]
                ).map((t) => {
                  const active = kindFilter === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setKindFilter(t.id)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        height: 26,
                        padding: '0 12px',
                        border: 'none',
                        borderRadius: 8,
                        cursor: 'pointer',
                        background: active ? '#fff' : 'transparent',
                        color: active ? 'var(--bx-text-dark)' : 'var(--bx-text-body)',
                        fontWeight: active ? 600 : 500,
                        fontSize: 12.5,
                        boxShadow: active ? 'var(--bx-shadow-pill)' : 'none',
                        fontFamily: 'var(--bx-font-sans)',
                      }}
                    >
                      {t.label}
                      <span
                        style={{
                          fontVariantNumeric: 'tabular-nums',
                          fontSize: 11,
                          color: active ? 'var(--bx-primary)' : 'var(--bx-text-muted)',
                        }}
                      >
                        {t.count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <Table
              columns={cols}
              rows={rows}
              getRowKey={(r) => r.id}
              onRowClick={(r) => navigate(KIND_LINK[r.kind])}
              emptyText="Không có config khớp bộ lọc"
              stickyHeader
            />
          </Card>
        )}
      </div>
    </div>
  );
}

function MonthlySparkline({ values }: { values: number[] }) {
  const max = Math.max(...values, 1);
  const todayMonth = new Date().getMonth() + 1;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 44 }}>
      {values.map((v, i) => {
        const h = (v / max) * 38;
        const isCurrent = i + 1 === todayMonth;
        const isPeak = v >= 1.25;
        const isLow = v < 0.9;
        const color = isPeak ? '#b91c1c' : isLow ? '#92400e' : 'var(--bx-primary)';
        return (
          <div
            key={i}
            title={`${MONTH_LABELS[i]} · ×${v.toFixed(2)}`}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <div
              style={{
                width: '100%',
                maxWidth: 14,
                height: h,
                background: color,
                opacity: isCurrent ? 1 : 0.6,
                borderRadius: 3,
              }}
            />
            <div
              style={{
                fontSize: 8.5,
                fontFamily: 'var(--bx-font-mono)',
                color: isCurrent ? color : 'var(--bx-text-muted)',
                fontWeight: isCurrent ? 700 : 400,
              }}
            >
              {MONTH_LABELS[i]}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Hint({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <div style={{ display: 'flex', gap: 12 }}>
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: '#eef2ff',
          color: 'var(--bx-primary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon name={icon} size={14} />
      </div>
      <div>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--bx-text-dark)',
            marginBottom: 4,
          }}
        >
          {title}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--bx-text-muted)', lineHeight: 1.5 }}>
          {body}
        </div>
      </div>
    </div>
  );
}
